import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { InkDot } from '@/components/InkDot';
import type { InkColor } from '@/lib/ink-utils';
import logoWordmark from '@/assets/logo-wordmark.png';

const INK_OPTIONS: InkColor[] = ['Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'];

interface AuthScreenProps {
  onSuccess?: () => void;
  compact?: boolean;
}

export function AuthScreen({ onSuccess, compact = false }: AuthScreenProps) {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [location, setLocation] = useState('');
  const [favoriteInk, setFavoriteInk] = useState<InkColor>('Amber');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'signup') {
        await signUp(email, password, username);
      } else {
        await signIn(email, password);
      }
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  const formContent = (
    <>
      {compact ? (
        <div className="text-center mb-4">
          <h3 className="font-display text-lg font-semibold text-foreground">Sign In</h3>
          <p className="text-xs text-muted-foreground font-body mt-1">Sign in or create an account to continue</p>
        </div>
      ) : (
        <div className="text-center mb-8 flex flex-col items-center">
          <img src={logoWordmark} alt="LoreScout logo" className="w-full object-contain" />
          <p className="mt-3 text-sm text-primary font-body">Your compass for everything Lorcana</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === 'signup' && (
          <>
            <div>
              <label htmlFor="auth-username" className="text-xs text-muted-foreground font-body">Username</label>
              <input id="auth-username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary" placeholder="Choose a username" required />
            </div>
            <div>
              <label htmlFor="auth-city" className="text-xs text-muted-foreground font-body">City</label>
              <input id="auth-city" type="text" value={location} onChange={(e) => setLocation(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary" placeholder="Your city" />
            </div>
            <div>
              <span className="text-xs text-muted-foreground font-body">Favorite Ink</span>
              <div className="mt-2 flex gap-2">
                {INK_OPTIONS.map((ink) => (
                  <button key={ink} type="button" onClick={() => setFavoriteInk(ink)} aria-label={`Favorite ink ${ink}`} aria-pressed={favoriteInk === ink} className={`flex flex-col items-center gap-1 rounded-lg p-2 transition-colors ${favoriteInk === ink ? 'bg-secondary ring-1 ring-primary' : 'hover:bg-secondary'}`}>
                    <InkDot ink={ink} size="md" />
                    <span className="text-[9px] text-muted-foreground font-body">{ink}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div>
          <label htmlFor="auth-email" className="text-xs text-muted-foreground font-body">Email</label>
          <input id="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary" placeholder="your@email.com" required />
        </div>
        <div>
          <label htmlFor="auth-password" className="text-xs text-muted-foreground font-body">Password</label>
          <input id="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground font-body focus:outline-none focus:ring-1 focus:ring-primary" placeholder="••••••••" required minLength={6} />
        </div>

        {error && <p className="text-xs text-destructive font-body">{error}</p>}

        <button type="submit" disabled={loading} className="w-full rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground font-body disabled:opacity-50">
          {loading ? '...' : mode === 'login' ? 'Sign In' : 'Create Account'}
        </button>
      </form>

      <button onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); }} className="mt-4 w-full text-center text-xs text-primary font-body">
        {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
      </button>
    </>
  );

  if (compact) {
    return formContent;
  }

  return (
    <div className="app-container flex min-h-screen flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">
        {formContent}
      </div>
    </div>
  );
}
